import type {
  InsightsProxyProvider,
  InsightsProxyReport,
} from "../bedrockApi/insightsProxy";
import type {
  ConnectionUi,
  IntegrationScopeKey,
} from "../generated/agentIntegration";
import type { ChatIntegrationObjectItem } from "../generated/chatMessage";
import type { IntegrationTag } from "../generated/integrationTag";
import type { ObjectTypeKey } from "../objectType";

import { z } from "zod";

import { objectTypeKeySchema } from "../chatMessage";

export type IntegrationKey = ChatIntegrationObjectItem["integrationKey"];

export type ReturnsObjectInfo =
  | { objectType: ObjectTypeKey; completeness: "full" }
  | {
      objectType: ObjectTypeKey;
      completeness: "partial";
      guaranteedFields: string[];
    };

export const integrationToolAnnotationsSchema = z.object({
  readOnlyHint: z.boolean(),
  destructiveHint: z.boolean(),
  idempotentHint: z.boolean(),
  openWorldHint: z.boolean(),
});

export type IntegrationToolAnnotations = z.infer<
  typeof integrationToolAnnotationsSchema
>;

export interface IntegrationToolDefinition {
  name: string;
  description: string;
  // The single source of truth for the tool's input contract: projected to
  // JSON Schema for advertisement and parsed at the MCP boundary.
  inputSchema: z.ZodObject<z.ZodRawShape>;
  outputSchema?: z.ZodType;
  returnsObject?: ReturnsObjectInfo;
  requiresPermission?: boolean;
  annotations?: Partial<IntegrationToolAnnotations>;
}

export interface NangoToolOverride {
  requiresPermission?: boolean;
  replacementToolName?: string;
  annotations?: Partial<IntegrationToolAnnotations>;
}

/**
 * Operations a child-object drilldown filter can use. Every value here must map
 * to a concrete `FilterClause` operator — see `buildChildObjectFilterClause` in
 * `integrationTools.ts`.
 */
export const filterOperationSchema = z.enum([
  "equals",
  "doesNotEqual",
  "contains",
  "in",
  "lt",
  "lte",
  "gt",
  "gte",
]);

/**
 * Describes a child object type that can be browsed by drilling into a parent
 * object (e.g. opusProject -> opusClip). The `filter` scopes the child search
 * to objects belonging to the selected parent.
 */
export const childObjectTypeSchema = z.object({
  type: objectTypeKeySchema,
  integrationKey: z.string().optional(),
  filter: z.object({
    dataModelKey: z.string(),
    operation: filterOperationSchema,
    valueFrom: z.enum(["id", "handle"]),
  }),
});

export type ChildObjectType = z.infer<typeof childObjectTypeSchema>;

export interface IntegrationObjectBinding {
  objectTypeKey: ObjectTypeKey;
  /** Plural label shown in the chat mention menu (e.g. "Products", "Clip Collections"). */
  pluralDisplayName: string;
  tags?: IntegrationTag[];
  browseGroup?: string;
  searchable?: {
    toolKey?: string;
    pageSize?: number;
    supportsQuery?: boolean;
  };
  childObjectTypes?: ChildObjectType[];
  showInMentionMenu?: boolean;
  suggestedLoaderKeys?: string[];
}

export interface DataLoaderDef {
  loaderKey: string;
  description: string;
  scaffoldComponentName: string;
  scaffoldImportPath: string;
  inputSchema: z.ZodType;
  outputSchema: z.ZodType;
  freshnessHintSeconds?: number;
  returnsObject?: ReturnsObjectInfo;
}

export interface CartCapabilityDef {
  persistenceKind: "externalCart" | "cookieCart";
  addToCartHook: string;
  addToCartHookImportPath: string;
  buyNowHook: string;
  buyNowHookImportPath: string;
  acceptedMerchandiseIdType: string;
  checkoutStrategy: "redirectToCheckoutUrl" | "createCheckoutSession";
}

type ConnectSessionConfigDefaultValue =
  | string
  | number
  | boolean
  | Record<string, string | number | boolean>;

export interface ConnectionFieldDefinition {
  key: string;
  label: string;
  type: "text" | "password";
  required: boolean;
  helpText?: string;
  /**
   * When set, non-empty trimmed input must match one of these values
   * (case-insensitive). Used by the connection form for enum-like fields
   * such as Reviews.io `apiRegion`.
   */
  allowedValues?: readonly string[];
}

interface IntegrationDefinitionBase {
  key: IntegrationKey;
  annotations?: Partial<IntegrationToolAnnotations>;
  name: string;
  description: string;
  usageGuidance: string;
  /**
   * Draft dropped into the chat composer after a first successful connect.
   * Write a complete, sendable sentence in the user's voice that only asks
   * for actions this integration's tools support. Omit to fall back to a
   * generic prompt built from `name`.
   */
  starterPrompt?: string;
  allowedScopes: [IntegrationScopeKey, ...IntegrationScopeKey[]];
  tags: IntegrationTag[];
  connectSessionConfigDefaults?: Record<
    string,
    ConnectSessionConfigDefaultValue
  >;
  /**
   * A first-party capability that ships with every project: no connect flow, no
   * Integrations tile, always reported as connected.
   */
  isBuiltIn?: boolean;
  nangoProviderConfigKey?: string;
  useNangoMcp?: boolean;
  connectionUi?: ConnectionUi;
  connectionFields?: ConnectionFieldDefinition[];
  connectionHighlights?: string[];
  /**
   * Top-level `customConfig` keys that hold secrets. Shorthand for
   * `secretConfigPaths` entries without dots — preferred for simple API-key
   * forms. Prefer `secretConfigPaths` for nested secrets (e.g. map values).
   */
  secretFields?: string[];
  /**
   * Dot-path pointers into `customConfig` whose string leaf values are
   * encrypted at rest (`enc:v1:`). Use `*` for “every key at this level”
   * (e.g. `deliveryTokensBySpaceId.*`). Combined with `secretFields` for
   * encrypt/decrypt; omit both when the integration stores no secrets here.
   */
  secretConfigPaths?: string[];
  tools: IntegrationToolDefinition[];
  toolOverrides?: Record<string, NangoToolOverride>;
  objects?: Record<string, IntegrationObjectBinding>;
  loaders?: Record<string, DataLoaderDef>;
  cart?: CartCapabilityDef;
  externalAnalytics?: {
    provider: InsightsProxyProvider;
    /**
     * The ClickHouse view(s) backing each report kind. A kind maps to more than
     * one view when the provider reports the same columns at more than one
     * grain — Intelligems has a sitewide view and a per-experience one.
     */
    views: Record<InsightsProxyReport, readonly string[]>;
  };
  image?: string;
  /** White/light mark used when the surrounding UI is dark. */
  darkImage?: string;
  docsUrl?: string;
  /**
   * Where the merchant goes to add Replo, for integrations whose connection is
   * made in the vendor's own UI ("external-mcp-client"). Vendor-owned and
   * opaque, so it lives with the rest of the vendor's static metadata rather
   * than being hardcoded in each client.
   */
  connectUrl?: string;
}

export interface NangoProxyIntegrationDefinitionMetadata
  extends IntegrationDefinitionBase {
  mode: "nango-proxy";
  nangoProviderConfigKey: string;
  authenticatedDownloadAllowedHosts?: string[];
}

export interface NangoSecretsIntegrationDefinitionMetadata
  extends IntegrationDefinitionBase {
  mode: "nango-secrets";
  nangoProviderConfigKey: string;
}

export interface CustomIntegrationDefinitionMetadata
  extends IntegrationDefinitionBase {
  mode: "custom";
}

export type IntegrationDefinitionMetadata =
  | NangoProxyIntegrationDefinitionMetadata
  | NangoSecretsIntegrationDefinitionMetadata
  | CustomIntegrationDefinitionMetadata;
